import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Globe2, Inbox, KanbanSquare, MailCheck, MapPinned, Ruler } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { get } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import { IntervalToggle, PayMethods, PlanCards } from "../components/billing/PlanCards";
import { usePub, useHead } from "./PublicSite";
import { InstallButton } from "../components/pwa/PwaUi";

const SCORES = [["design", 6.1], ["seo", 4.2], ["speed", 3.4], ["mobile", 5.8], ["trust", 7.0]];
const tone = (v) => (v < 5 ? "bad" : v < 7 ? "warn" : "good");

const ProductVisual = () => {
  const { pt } = usePub();
  const { fmtNum } = useI18n();
  return (
    <div className="pub-visual" aria-hidden="true" data-testid="hero-visual">
      <div className="pub-card pub-audit">
        <div className="pub-audit-head">
          <span className="pub-live"><i />{pt("home.demo_label")}</span>
          <span className="font-mono text-xs" dir="ltr">{pt("home.demo_site")}</span>
        </div>
        <div className="pub-audit-body">
          <div className="pub-ring" style={{ "--v": 52 }}><span className="font-mono">{fmtNum(5.2, 1)}</span><small>{pt("home.demo_overall")}</small></div>
          <ul className="pub-bars">
            {SCORES.map(([k, v]) => (
              <li key={k}><span>{pt(`home.demo_${k}`)}</span><b className={`is-${tone(v)}`} style={{ width: `${v * 10}%` }} /><em className="font-mono">{fmtNum(v, 1)}</em></li>
            ))}
          </ul>
        </div>
        <ol className="pub-findings">{["f1", "f2", "f3"].map((f, i) => <li key={f}><span className={`pub-dot is-${i < 2 ? "bad" : "warn"}`} />{pt(`home.demo_${f}`)}</li>)}</ol>
      </div>
      <div className="pub-card pub-email">
        <p className="pub-email-label"><MailCheck className="h-4 w-4" />{pt("home.demo_email_label")}</p>
        <p className="pub-email-subject">{pt("home.demo_email_subject")}</p>
        <p className="pub-email-body">{pt("home.demo_email_body")}</p>
      </div>
    </div>
  );
};

export const PricingBlock = () => {
  const { lang } = usePub();
  const { me } = useAuth();
  const navigate = useNavigate();
  const [interval, setIntervalV] = useState("annual");
  const { data } = useFetch(() => get("/billing/plans"), []);
  if (!data) return <div className="pub-plans-skeleton" />;
  const select = (plan) => {
    if (plan === "free") return navigate(me ? "/dashboard" : "/signup");
    const q = `plan=${plan}&interval=${interval}`;
    return navigate(me ? `/billing?${q}` : `/signup?${q}`);
  };
  return (
    <div className="pub-pricing" data-lang={lang}>
      <div className="pub-toggle-sticky flex justify-center"><IntervalToggle value={interval} onChange={setIntervalV} /></div>
      <PlanCards plans={data.plans} interval={interval} onSelect={select} />
      <PayMethods className="justify-center" />
    </div>
  );
};

const ICONS = [Ruler, Globe2, MailCheck, Inbox, KanbanSquare, MapPinned];

export default function Landing() {
  const { pt, lang } = usePub();
  useHead(pt("meta.home_title"), pt("meta.home_desc"));
  return (
    <div data-testid="landing-page">
      <section className="pub-hero" data-sticky-anchor>
        <div className="pub-grid-bg" />
        <div className="pub-wrap pub-hero-grid">
          <div className="pub-hero-copy">
            <p className="pub-kicker">{pt("home.kicker")}</p>
            <h1 className="pub-h1" data-testid="landing-title">{pt("home.title")}</h1>
            <p className="pub-lead">{pt("home.sub")}</p>
            <div className="pub-cta-row">
              <Link to="/signup" className="pub-btn pub-btn-accent pub-btn-lg" data-testid="hero-cta-signup">{pt("home.cta")}<ArrowRight className="am-flip h-5 w-5" /></Link>
              <a href="#how" className="pub-btn pub-btn-outline pub-btn-lg" data-testid="hero-cta-how">{pt("home.cta2")}</a>
              <InstallButton className="pub-btn pub-btn-outline pub-btn-lg pub-desktop-only" id="landing-install-button" />
            </div>
            <p className="pub-note">{pt("home.note")}</p>
          </div>
          <ProductVisual />
        </div>
      </section>
      <section id="how" className="pub-section">
        <div className="pub-wrap">
          <h2 className="pub-h2">{pt("home.how_title")}</h2>
          <ol className="pub-steps">{[1, 2, 3, 4].map((i) => (
            <li key={i} data-testid={`how-step-${i}`}><span className="pub-step-n font-mono">0{i}</span><h3>{pt(`home.how${i}_t`)}</h3><p>{pt(`home.how${i}_b`)}</p></li>
          ))}</ol>
        </div>
      </section>
      <section className="pub-section pub-dark">
        <div className="pub-wrap">
          <h2 className="pub-h2">{pt("home.feat_title")}</h2>
          <p className="pub-sub">{pt("home.feat_sub")}</p>
          <div className="pub-feats">{ICONS.map((Icon, i) => (
            <article key={i} className="pub-feat" data-testid={`feature-${i + 1}`}><Icon className="h-6 w-6" /><h3>{pt(`home.f${i + 1}_t`)}</h3><p>{pt(`home.f${i + 1}_b`)}</p></article>
          ))}</div>
          <Link to={`/${lang}/features`} className="pub-link-light" data-testid="landing-features-link">{pt("nav.features")} <ArrowRight className="am-flip inline h-4 w-4" /></Link>
        </div>
      </section>
      <section className="pub-section" id="pricing">
        <div className="pub-wrap">
          <h2 className="pub-h2 text-center">{pt("home.pricing_title")}</h2>
          <p className="pub-sub text-center">{pt("home.pricing_sub")}</p>
          <PricingBlock />
        </div>
      </section>
      <section className="pub-section pub-soft">
        <div className="pub-wrap pub-faq-wrap">
          <h2 className="pub-h2">{pt("home.faq_title")}</h2>
          <div className="pub-faq">{[1, 2, 3, 4, 5, 6].map((i) => (
            <details key={i} data-testid={`faq-${i}`}><summary>{pt(`home.q${i}`)}</summary><p>{pt(`home.a${i}`)}</p></details>
          ))}</div>
        </div>
      </section>
      <section className="pub-section pub-final">
        <div className="pub-wrap text-center">
          <h2 className="pub-h2">{pt("home.cta_title")}</h2>
          <p className="pub-sub">{pt("home.cta_sub")}</p>
          <Link to="/signup" className="pub-btn pub-btn-accent pub-btn-lg" data-testid="final-cta-signup">{pt("home.cta_button")}<ArrowRight className="am-flip h-5 w-5" /></Link>
        </div>
      </section>
    </div>
  );
}
